import {
  CARGO_ONBOARD_IMO_HAZARD_CLASSES_MAX_LENGTH,
  CARGO_ONBOARD_QUANTITY_MAX_DECIMALS,
  CARGO_ONBOARD_QUANTITY_MAX_DIGITS,
  shouldShowCargoOnboardSection,
} from "@features/vessel-call/cargoOnboard";
import {
  CALL_ADDITIONAL_DOCUMENT_TYPES,
  FORM_FIELDS,
  methodAtImo2020ComplianceValues,
  NATURE_OF_CALL,
  NATURE_OF_CALL_SPECIFY,
} from "@features/vessel-call/create/constants";
import { hasAnyPortCallDateConflicts } from "@features/vessel-call/create/steps/PreArrivalNotification/portCallDateValidation";
import { toDate, toMinute } from "@utils/date-time";
import { t, type TFunction } from "i18next";
import { z } from "zod";

const LANG_BASE = "vessel-call.create.form-fields";
const MANDATORY_KEY = "form-validation.mandatory";
const OTHER_SPECIFY_LABEL_KEY = "vessel-call.additional-documents.other-specify";

type NatureOfCallField = (typeof NATURE_OF_CALL)[keyof typeof NATURE_OF_CALL];

const natureOfCallBooleansShape: Record<
  NatureOfCallField,
  z.ZodOptional<z.ZodBoolean>
> = Object.values(NATURE_OF_CALL).reduce(
  (acc, field) => {
    acc[field] = z.boolean().optional();
    return acc;
  },
  {} as Record<NatureOfCallField, z.ZodOptional<z.ZodBoolean>>,
);

type SpecifyField = (typeof NATURE_OF_CALL_SPECIFY)[keyof typeof NATURE_OF_CALL_SPECIFY];

const specifyFieldsShape: Record<SpecifyField, z.ZodOptional<z.ZodString>> = Object.values(
  NATURE_OF_CALL_SPECIFY,
).reduce(
  (acc, field) => {
    acc[field] = z
      .string()
      .max(
        100,
        t("form-validation.max-length", {
          label: t(`${LANG_BASE}.${field}`),
          max: 100,
        }),
      )
      .optional();
    return acc;
  },
  {} as Record<SpecifyField, z.ZodOptional<z.ZodString>>,
);

const specifyDependencies: Array<{ flag: NatureOfCallField; specify: SpecifyField }> = [
  { flag: NATURE_OF_CALL.CARGO_OPERATION, specify: NATURE_OF_CALL_SPECIFY.CARGO_OPETATION_SPECIFY },
  { flag: NATURE_OF_CALL.WASTE_DISPOSAL, specify: NATURE_OF_CALL_SPECIFY.WASTE_DISPOSAL_SPECIFY },
  { flag: NATURE_OF_CALL.OTHERS, specify: NATURE_OF_CALL_SPECIFY.OTHERS_SPECIFY },
];

const requiredDate = (label: string) =>
  z.union([
    z.date({
      required_error: t(MANDATORY_KEY, { label }),
      invalid_type_error: t(MANDATORY_KEY, { label }),
    }),
    z.string().min(1, t(MANDATORY_KEY, { label })),
  ]);

const optionalDate = () => z.union([z.date().nullable().optional(), z.string()]);

const requiredString = (label: string) => z.string().min(1, t(MANDATORY_KEY, { label }));

const requiredStringMax = (max: number, label: string) =>
  requiredString(label).max(max, t("form-validation.max-length", { label, max }));

const optionalStringMax = (max: number, label: string) =>
  z.string().max(max, t("form-validation.max-length", { label, max })).optional();

const optionalIntNumberMax = (maxDigits: number, label: string) =>
  z
    .string()
    .max(maxDigits, t("form-validation.max-length-digits", { label, maxDigits }))
    .optional();

const ALPHANUM_REGEX = /^[A-Za-z0-9\s]*$/;
const CALL_SIGN_REGEX = /^[A-Z0-9]*$/;

const isNonEmpty = (v: unknown) => typeof v === "string" && v.trim().length > 0;

const parseDocumentDate = (value?: Date | string | null): Date | null => {
  if (!value) return null;
  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  parsed.setHours(0, 0, 0, 0);
  return parsed;
};

const normalizeDraftEmptyValue = (value: unknown) => {
  if (value == null) return undefined;
  if (typeof value === "string" && value.trim().length === 0) return undefined;
  if (Array.isArray(value) && value.length === 0) return undefined;
  return value;
};

const optionalInDraft = <T extends z.ZodTypeAny>(schema: T, isDraft?: boolean) =>
  isDraft ? z.preprocess(normalizeDraftEmptyValue, schema.optional()) : schema;

const callAdditionalDocumentSchema = (isDraft?: boolean) =>
  z
    .object({
      [FORM_FIELDS.DOCUMENT_URL]: z.string(),
      [FORM_FIELDS.FILE_NAME]: z.string().optional(),
      [FORM_FIELDS.FILE_FORMAT]: z.string().optional(),
      [FORM_FIELDS.DOCUMENT_TYPE]: z.string().optional(),
      [FORM_FIELDS.OTHER_SPECIFY_TYPE]: z
        .string()
        .max(
          50,
          t("form-validation.max-length", {
            label: t(OTHER_SPECIFY_LABEL_KEY),
            max: 50,
          }),
        )
        .optional(),
      [FORM_FIELDS.DOCUMENT_NAME]: z
        .string()
        .max(
          50,
          t("form-validation.max-length", {
            label: t("vessel-call.additional-documents.document-name"),
            max: 50,
          }),
        )
        .optional(),
      [FORM_FIELDS.ISSUE_DATE]: optionalDate(),
      [FORM_FIELDS.DOCUMENT_SUMMARY]: z
        .string()
        .max(
          300,
          t("form-validation.max-length", {
            label: t("vessel-call.additional-documents.document-summary"),
            max: 300,
          }),
        )
        .optional(),
      [FORM_FIELDS.EXPIRY_DATE]: optionalDate(),
    })
    .superRefine((document, ctx) => {
      if (!isDraft) {
        if (!document.documentType?.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t("form-validation.required", {
              label: t("vessel-call.additional-documents.document-type"),
            }),
            path: [FORM_FIELDS.DOCUMENT_TYPE],
          });
        }

        if (!document.documentName?.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t("form-validation.required", {
              label: t("vessel-call.additional-documents.document-name"),
            }),
            path: [FORM_FIELDS.DOCUMENT_NAME],
          });
        }

        if (
          document.documentType === CALL_ADDITIONAL_DOCUMENT_TYPES.OTHER_SPECIFY &&
          !document.otherSpecifyType?.trim()
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t("form-validation.required", {
              label: t(OTHER_SPECIFY_LABEL_KEY),
            }),
            path: [FORM_FIELDS.OTHER_SPECIFY_TYPE],
          });
        }

        if (
          document.documentType === CALL_ADDITIONAL_DOCUMENT_TYPES.CERTIFICATE &&
          !document.expiryDate
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t("form-validation.required", {
              label: t("vessel-call.additional-documents.expiry-date"),
            }),
            path: [FORM_FIELDS.EXPIRY_DATE],
          });
        }
      }

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const issueDate = parseDocumentDate(document.issueDate);
      const expiryDate = parseDocumentDate(document.expiryDate);

      if (issueDate && issueDate > today) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("form-validation.must-not-be-in-future", {
            label: t("vessel-call.additional-documents.issue-date"),
          }),
          path: [FORM_FIELDS.ISSUE_DATE],
        });
      }

      if (expiryDate && expiryDate < today) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("form-validation.must-not-be-in-past", {
            label: t("vessel-call.additional-documents.expiry-date"),
          }),
          path: [FORM_FIELDS.EXPIRY_DATE],
        });
      }

      if (issueDate && expiryDate && issueDate > expiryDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("form-validation.must-be-after", {
            subject: t("vessel-call.additional-documents.expiry-date"),
            object: t("vessel-call.additional-documents.issue-date"),
          }),
          path: [FORM_FIELDS.EXPIRY_DATE],
        });
      }
    });

const requiredNumberMax = (maxDigits: number, maxDecimals: number, label: string, minValue = 0) =>
  z.preprocess(
    (val) => (typeof val === "number" ? val.toString() : val),
    z
      .string()
      .optional()
      .superRefine((val, ctx) => {
        if (!val || val.trim().length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t("form-validation.mandatory", { label }),
          });
          return;
        }

        if (!/^\d+(\.\d+)?$/.test(val)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t("form-validation.invalid-number", { label }),
          });
          return;
        }

        const [intPart, decPart] = val.split(".");

        if (intPart.length > maxDigits) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t("form-validation.max-length-digits", { label, maxDigits }),
          });
        }

        if (decPart && decPart.length > maxDecimals) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t("form-validation.max-length-decimals", { label, maxDecimals }),
          });
        }

        const asNumber = Number(val);
        if (Number.isFinite(asNumber) && asNumber < minValue) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t("form-validation.must-be-greater", { label, min: 0 }),
          });
        }
      }),
  );

const optionalNumberMax = (maxDigits: number, maxDecimals: number, label: string, minValue = 0) =>
  z.preprocess(
    (val) => (typeof val === "number" ? val.toString() : val),
    z
      .string()
      .optional()
      .superRefine((val, ctx) => {
        if (!val || val.trim().length === 0) {
          return; // allow empty
        }

        if (!/^\d+(\.\d+)?$/.test(val)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t("form-validation.invalid-number", { label }),
          });
          return;
        }

        const [intPart, decPart] = val.split(".");

        if (intPart.length > maxDigits) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t("form-validation.max-length-digits", { label, maxDigits }),
          });
        }

        if (decPart && decPart.length > maxDecimals) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t("form-validation.max-length-decimals", { label, maxDecimals }),
          });
        }

        const asNumber = Number(val);
        if (Number.isFinite(asNumber) && asNumber < minValue) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t("form-validation.must-be-greater", { label, min: 0 }),
          });
        }
      }),
  );

export const getCargoOnboardItemSchema = (t: TFunction, options?: { requireFields?: boolean }) =>
  z
    .object({
      id: z.string(),
      cargoType: z.string().optional(),
      cargoTypeDescription: z.string().optional(),
      quantity: z.preprocess(
        (val) => (typeof val === "number" ? val.toString() : val),
        z
          .string()
          .optional()
          .superRefine((val, ctx) => {
            if (!val || val.trim().length === 0) {
              return;
            }

            const label = t(`${LANG_BASE}.quantity`);

            if (!/^\d+(\.\d+)?$/.test(val)) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: t("form-validation.invalid-number", { label }),
              });
              return;
            }

            const [intPart, decPart] = val.split(".");

            if (intPart.length > CARGO_ONBOARD_QUANTITY_MAX_DIGITS) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: t("form-validation.max-length-digits", {
                  label,
                  maxDigits: CARGO_ONBOARD_QUANTITY_MAX_DIGITS,
                }),
              });
            }

            if (decPart && decPart.length > CARGO_ONBOARD_QUANTITY_MAX_DECIMALS) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: t("form-validation.max-length-decimals", {
                  label,
                  maxDecimals: CARGO_ONBOARD_QUANTITY_MAX_DECIMALS,
                }),
              });
            }

            const asNumber = Number(val);
            if (Number.isFinite(asNumber) && asNumber < 0) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: t("form-validation.must-be-greater", { label, min: 0 }),
              });
            }
          }),
      ),
      unitOfMeasurement: z.string().optional(),
      unitOfMeasurementDescription: z.string().optional(),
      hazardousAndDangerousCargo: z.boolean(),
      imoHazardClasses: z
        .string()
        .max(
          CARGO_ONBOARD_IMO_HAZARD_CLASSES_MAX_LENGTH,
          t("form-validation.max-length", {
            label: t(`${LANG_BASE}.imoHazardClasses`),
            max: CARGO_ONBOARD_IMO_HAZARD_CLASSES_MAX_LENGTH,
          }),
        )
        .optional(),
    })
    .superRefine((item, ctx) => {
      if (!options?.requireFields) {
        return;
      }

      if (!isNonEmpty(item.cargoType)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["cargoType"],
          message: t(MANDATORY_KEY, {
            label: t(`${LANG_BASE}.cargoType`),
          }),
        });
      }

      if (!isNonEmpty(String(item.quantity ?? ""))) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["quantity"],
          message: t(MANDATORY_KEY, {
            label: t(`${LANG_BASE}.quantity`),
          }),
        });
      }

      if (!isNonEmpty(item.unitOfMeasurement)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["unitOfMeasurement"],
          message: t(MANDATORY_KEY, {
            label: t(`${LANG_BASE}.unitOfMeasurement`),
          }),
        });
      }

      if (item.hazardousAndDangerousCargo && !isNonEmpty(item.imoHazardClasses)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["imoHazardClasses"],
          message: t(MANDATORY_KEY, {
            label: t(`${LANG_BASE}.imoHazardClasses`),
          }),
        });
      }
    });

export const refineRequiredDateTime =
  (label: string, messageKey: string) =>
  <T extends z.ZodType<Date | null | string>>(
    schema: T,
  ): z.ZodEffects<T, Date | null | string, Date | null | string> =>
    schema.refine(
      (val) => {
        const dMin = toMinute(val);
        if (!dMin) return true;
        const nowMin = toMinute(new Date())!;
        return dMin.getTime() >= nowMin.getTime();
      },
      { message: t(messageKey, { label }) },
    );

const requiredNotPastDateTime = (label: string) =>
  refineRequiredDateTime(label, "form-validation.must-not-be-in-past")(requiredDate(label));

const documentSchema = z.object({
  name: z.string(),
  url: z.string(),
  type: z.string(),
});

const optionalDocument = z.array(documentSchema).optional();

const requiredDocument = z.array(documentSchema);

const startOfDay = (d: Date) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

const today = () => startOfDay(new Date());
const leToday = (d: Date) => d <= today();
const geToday = (d: Date) => d >= today();

const refineRequiredDate =
  (label: string, pass: (d: Date) => boolean, messageKey: string) => (schema: z.ZodTypeAny) =>
    schema.refine(
      (val) => {
        const d = toDate(val);
        if (!d) return true;
        return pass(startOfDay(d));
      },
      { message: t(messageKey, { label }) },
    );

const refineOptionalDate =
  (label: string, pass: (d: Date) => boolean, messageKey: string) => (schema: z.ZodTypeAny) =>
    schema.refine(
      (val) => {
        if (val == null || val === "") return true;
        const d = toDate(val);
        if (!d) return false;
        return pass(startOfDay(d));
      },
      { message: t(messageKey, { label }) },
    );

const optionalPastDate = (label: string) =>
  refineOptionalDate(label, leToday, "form-validation.must-be-in-past")(optionalDate());

const optionalFutureDate = (label: string) =>
  refineOptionalDate(label, geToday, "form-validation.must-be-in-future")(optionalDate());

const requiredPastDate = (label: string) =>
  refineRequiredDate(label, leToday, "form-validation.must-be-in-past")(requiredDate(label));

export const getVesselCallFormSchema = (
  t: TFunction,
  options?: {
    isFinalSubmission?: boolean;
    isDraft?: boolean;
  },
) => {
  const isDraft = options?.isDraft === true;
  const isFinalSubmission = options?.isFinalSubmission === true && !isDraft;

  return z
    .object({
      [FORM_FIELDS.VESSEL_ID]: z.string(),
      [FORM_FIELDS.SELECT_PORT]: optionalInDraft(
        requiredString(t(`${LANG_BASE}.${FORM_FIELDS.SELECT_PORT}`)),
        isDraft,
      ),
      [FORM_FIELDS.EXPECTED_ARRIVAL_LOCATION]: optionalInDraft(
        requiredString(t(`${LANG_BASE}.${FORM_FIELDS.EXPECTED_ARRIVAL_LOCATION}`)),
        isDraft,
      ),
      [FORM_FIELDS.PREFERRED_ANCHORAGE_AREA]: z.string().optional(),
      [FORM_FIELDS.REASON_FOR_ANCHORAGE_PREFERENCE]: optionalStringMax(
        100,
        t(`${LANG_BASE}.${FORM_FIELDS.REASON_FOR_ANCHORAGE_PREFERENCE}`),
      ),
      [FORM_FIELDS.MASTERS_NAME]: isFinalSubmission
        ? requiredStringMax(50, t(`${LANG_BASE}.${FORM_FIELDS.MASTERS_NAME}`))
        : optionalStringMax(50, t(`${LANG_BASE}.${FORM_FIELDS.MASTERS_NAME}`)),
      [FORM_FIELDS.NATIONALITY]: isFinalSubmission
        ? requiredString(t(`${LANG_BASE}.${FORM_FIELDS.NATIONALITY}`))
        : z.string().optional(),
      [FORM_FIELDS.ETA]: optionalInDraft(
        requiredNotPastDateTime(t(`${LANG_BASE}.${FORM_FIELDS.ETA}`)),
        isDraft,
      ),
      [FORM_FIELDS.ETD]: isFinalSubmission
        ? requiredNotPastDateTime(t(`${LANG_BASE}.${FORM_FIELDS.ETD}`))
        : optionalFutureDate(t(`${LANG_BASE}.${FORM_FIELDS.ETD}`)),
      [FORM_FIELDS.OWNERS_MATTERS_AGENT]: isFinalSubmission
        ? requiredString(t(`${LANG_BASE}.${FORM_FIELDS.OWNERS_MATTERS_AGENT}`))
        : z.string().optional(),
      [FORM_FIELDS.LAST_PORT]: optionalInDraft(
        requiredString(t(`${LANG_BASE}.${FORM_FIELDS.LAST_PORT}`)),
        isDraft,
      ),
      [FORM_FIELDS.NEXT_PORT]: optionalInDraft(
        z.string().min(1, {
          message: t(MANDATORY_KEY, {
            label: t(`${LANG_BASE}.${FORM_FIELDS.NEXT_PORT}`),
          }),
        }),
        isDraft,
      ),
      [FORM_FIELDS.DRAFT_AFT]: isFinalSubmission
        ? requiredNumberMax(15, 3, t(`${LANG_BASE}.${FORM_FIELDS.DRAFT_AFT}`))
        : optionalNumberMax(15, 3, t(`${LANG_BASE}.${FORM_FIELDS.DRAFT_AFT}`)),
      [FORM_FIELDS.DRAFT_FWD]: isFinalSubmission
        ? requiredNumberMax(15, 3, t(`${LANG_BASE}.${FORM_FIELDS.DRAFT_FWD}`))
        : optionalNumberMax(15, 3, t(`${LANG_BASE}.${FORM_FIELDS.DRAFT_FWD}`)),
      [FORM_FIELDS.VESSEL_CONDITION]: optionalInDraft(
        z.string().min(1, {
          message: t(MANDATORY_KEY, {
            label: t(`${LANG_BASE}.${FORM_FIELDS.VESSEL_CONDITION}`),
          }),
        }),
        isDraft,
      ),
      [FORM_FIELDS.CARGO_ONBOARD]: z.array(getCargoOnboardItemSchema(t)).default([]),
      ...natureOfCallBooleansShape,
      ...specifyFieldsShape,
      [FORM_FIELDS.METHOD_AT_IMO_2020_COMPLIANCE]: optionalInDraft(
        z.enum(Object.values(methodAtImo2020ComplianceValues) as [string, ...string[]]),
        isDraft,
      ),
      [FORM_FIELDS.PORT_FACILITY]: isFinalSubmission
        ? requiredString(t(`${LANG_BASE}.${FORM_FIELDS.PORT_FACILITY}`))
        : z.string().optional(),
      [FORM_FIELDS.PORT_FACILITY_OTHER]: optionalStringMax(
        100,
        t(`${LANG_BASE}.${FORM_FIELDS.PORT_FACILITY_OTHER}`),
      ),
      [FORM_FIELDS.PRIMARY_NATURE_OF_CALL_CODE]: optionalInDraft(
        requiredString(t(`${LANG_BASE}.${FORM_FIELDS.PRIMARY_NATURE_OF_CALL_CODE}`)),
        isDraft,
      ),
      [FORM_FIELDS.PRIMARY_NATURE_OF_CALL_DESC]: z.string().optional(),
      [FORM_FIELDS.SECURITY_LEVEL]: isFinalSubmission
        ? requiredString(t(`${LANG_BASE}.${FORM_FIELDS.SECURITY_LEVEL}`))
        : z.string().optional(),
      [FORM_FIELDS.ISSC_IS_VALID]: z.boolean(),
      [FORM_FIELDS.ISSC_NUMBER]: optionalStringMax(
        30,
        t(`${LANG_BASE}.${FORM_FIELDS.ISSC_NUMBER}`),
      ),
      [FORM_FIELDS.ISSC_ISSUE_DATE]: optionalPastDate(
        t(`${LANG_BASE}.${FORM_FIELDS.ISSC_ISSUE_DATE}`),
      ),
      [FORM_FIELDS.ISSC_EXPIRY_DATE]: optionalFutureDate(
        t(`${LANG_BASE}.${FORM_FIELDS.ISSC_EXPIRY_DATE}`),
      ),
      [FORM_FIELDS.ISSC_ISSUED_BY]: optionalStringMax(
        100,
        t(`${LANG_BASE}.${FORM_FIELDS.ISSC_ISSUED_BY}`),
      ),
      [FORM_FIELDS.APPROVED_SSP_ONBOARD]: z.boolean(),
      [FORM_FIELDS.SPECIAL_SECURITY_MEASURES]: z.boolean(),
      [FORM_FIELDS.TAKEN_MEASURES]: optionalStringMax(
        300,
        t(`${LANG_BASE}.${FORM_FIELDS.TAKEN_MEASURES}`),
      ),
      [FORM_FIELDS.SECURITY_GUARDS_ON_BOARD]: z.boolean(),
      [FORM_FIELDS.NUMBER_OF_SECURITY_GUARDS]: optionalIntNumberMax(
        5,
        t(`${LANG_BASE}.${FORM_FIELDS.NUMBER_OF_SECURITY_GUARDS}`),
      ),
      [FORM_FIELDS.ARM_AMMUNITION_ON_BOARD]: z.boolean(),
      [FORM_FIELDS.VESSEL_HAS_DEFICIENCIES]: z.boolean(),
      [FORM_FIELDS.DEFICIENCIES]: optionalStringMax(
        300,
        t(`${LANG_BASE}.${FORM_FIELDS.DEFICIENCIES}`),
      ),
      [FORM_FIELDS.LAST_PORT_CLEARANCE_ISSUE_DATE]: isFinalSubmission
        ? requiredPastDate(t(`${LANG_BASE}.${FORM_FIELDS.LAST_PORT_CLEARANCE_ISSUE_DATE}`))
        : optionalPastDate(t(`${LANG_BASE}.${FORM_FIELDS.LAST_PORT_CLEARANCE_ISSUE_DATE}`)),
      [FORM_FIELDS.LAST_PORT_CLEARANCE_CLASS_NAME]: isFinalSubmission
        ? requiredStringMax(
            30,
            t(`${LANG_BASE}.${FORM_FIELDS.LAST_PORT_CLEARANCE_CLASS_NAME}`),
          ).regex(
            ALPHANUM_REGEX,
            t("form-validation.regex-no-special-chars", {
              label: t(`${LANG_BASE}.${FORM_FIELDS.LAST_PORT_CLEARANCE_CLASS_NAME}`),
            }),
          )
        : z
            .string()
            .max(30, t(`${LANG_BASE}.${FORM_FIELDS.LAST_PORT_CLEARANCE_CLASS_NAME}`))
            .regex(
              ALPHANUM_REGEX,
              t("form-validation.regex-no-special-chars", {
                label: t(`${LANG_BASE}.${FORM_FIELDS.LAST_PORT_CLEARANCE_CLASS_NAME}`),
              }),
            )
            .optional(),

      [FORM_FIELDS.FLAG_STATE_PERMISSION_DOCUMENT_URL]: optionalDocument,
      [FORM_FIELDS.ARM_AMMUNITION_PROOF_DOCUMENT_URL]: optionalDocument,
      [FORM_FIELDS.DEFICIENCIES_DOCUMENTS_URL]: optionalDocument,
      [FORM_FIELDS.LAST_PORT_CLEARANCE_DOCUMENT_URL]: isFinalSubmission
        ? requiredDocument
        : optionalDocument,

      [FORM_FIELDS.ADDITIONAL_REMARKS]: optionalStringMax(
        1000,
        t("vessel-call.additional-documents.remarks-instructions"),
      ),
      [FORM_FIELDS.CALL_ADDITIONAL_DOCUMENTS]: z
        .array(callAdditionalDocumentSchema(isDraft))
        .optional(),

      [FORM_FIELDS.NAME_AND_TYPE_OF_SHIP]: optionalInDraft(
        requiredStringMax(80, t("vessel-call.falform1.name-and-type-of-ship")),
        isDraft,
      ),
      [FORM_FIELDS.IMO_NUMBER]: optionalInDraft(
        requiredNumberMax(8, 0, t("vessel-call.falform1.imo-number")),
        isDraft,
      ),
      [FORM_FIELDS.CALL_SIGN]: isFinalSubmission
        ? requiredStringMax(10, t("vessel-call.falform1.call-sign")).regex(
            CALL_SIGN_REGEX,
            t("form-validation.no-special-characters-allowed"),
          )
        : z
            .string()
            .max(10, t("vessel-call.falform1.call-sign"))
            .regex(CALL_SIGN_REGEX, t("form-validation.no-special-characters-allowed"))
            .optional(),
      [FORM_FIELDS.VOYAGE_NUMBER]: isFinalSubmission
        ? requiredStringMax(20, t("vessel-call.falform1.voyage-number"))
        : optionalStringMax(20, t("vessel-call.falform1.voyage-number")),

      [FORM_FIELDS.PORT_OF_ARRIVAL]: isFinalSubmission
        ? requiredStringMax(200, t("vessel-call.falform1.port-of-arrival"))
        : optionalStringMax(200, t("vessel-call.falform1.port-of-arrival")),
      [FORM_FIELDS.DATE_TIME_OF_ARRIVAL]: isFinalSubmission
        ? requiredNotPastDateTime(t("vessel-call.falform1.date-time-of-arrival"))
        : optionalFutureDate(t("vessel-call.falform1.date-time-of-arrival")),

      [FORM_FIELDS.FLAG_STATE_OF_SHIP]: isFinalSubmission
        ? requiredStringMax(20, t("vessel-call.falform1.flag-state-of-ship"))
        : optionalStringMax(20, t("vessel-call.falform1.flag-state-of-ship")),
      [FORM_FIELDS.NAME_OF_MASTER]: isFinalSubmission
        ? requiredStringMax(50, t("vessel-call.falform1.name-of-master"))
        : optionalStringMax(50, t("vessel-call.falform1.name-of-master")),
      [FORM_FIELDS.LAST_PORT_OF_CALL]: isFinalSubmission
        ? requiredStringMax(200, t("vessel-call.falform1.last-port-of-call"))
        : optionalStringMax(200, t("vessel-call.falform1.last-port-of-call")),
      [FORM_FIELDS.CERTIFICATE_OF_REGISTRY]: isFinalSubmission
        ? requiredStringMax(100, t("vessel-call.falform1.certificate-of-registry"))
        : optionalStringMax(100, t("vessel-call.falform1.certificate-of-registry")),
      [FORM_FIELDS.SHIP_AGENT_CONTACT]: isFinalSubmission
        ? requiredStringMax(200, t("vessel-call.falform1.ship-agent-contact"))
        : optionalStringMax(200, t("vessel-call.falform1.ship-agent-contact")),
      [FORM_FIELDS.GROSS_TONNAGE]: isFinalSubmission
        ? requiredNumberMax(15, 0, t("vessel-call.falform1.gross-tonnage"), 1)
        : optionalNumberMax(15, 0, t("vessel-call.falform1.gross-tonnage")),
      [FORM_FIELDS.NET_TONNAGE]: isFinalSubmission
        ? requiredNumberMax(15, 0, t("vessel-call.falform1.net-tonnage"), 1)
        : optionalNumberMax(15, 0, t("vessel-call.falform1.net-tonnage")),
      [FORM_FIELDS.VOYAGE_PARTICULARS]: isFinalSubmission
        ? requiredStringMax(1000, t("vessel-call.falform1.voyage-particulars-short"))
        : optionalStringMax(1000, t("vessel-call.falform1.voyage-particulars-short")),
      [FORM_FIELDS.CARGO_DESCRIPTION]: isFinalSubmission
        ? requiredStringMax(1000, t("vessel-call.falform1.cargo-description"))
        : optionalStringMax(1000, t("vessel-call.falform1.cargo-description")),
      [FORM_FIELDS.PORT_CALLS]: isFinalSubmission
        ? z
            .array(
              z.object({
                port: z.string().max(100),
                country: z.string().max(100),
                securityLevelOfPortCall: z.string().max(100),
                arrivalDate: z.string().max(100),
                departureDate: z.string().max(100),
                comments: z.string().max(500),
              }),
            )
            .min(1, {
              message: t("vessel-call.last-ten-ports.validation.required"),
            })
        : z
            .array(
              z.object({
                port: z.string().max(100),
                country: z.string().max(100),
                securityLevelOfPortCall: z.string().max(100),
                arrivalDate: z.string().max(100),
                departureDate: z.string().max(100),
                comments: z.string().max(500),
              }),
            )
            .optional(),
      [FORM_FIELDS.NUMBER_OF_CREW]: isFinalSubmission
        ? requiredNumberMax(15, 0, t("vessel-call.falform1.number-of-crew"), 0)
        : optionalNumberMax(15, 0, t("vessel-call.falform1.number-of-crew")),
      [FORM_FIELDS.NUMBER_OF_PASSENGERS]: isFinalSubmission
        ? requiredNumberMax(15, 0, t("vessel-call.falform1.number-of-passengers"), 0)
        : optionalNumberMax(15, 0, t("vessel-call.falform1.number-of-passengers")),
      [FORM_FIELDS.REMARKS]: optionalStringMax(500, t("vessel-call.falform1.remarks")).optional(),
      [FORM_FIELDS.WASTERESIDUES_REQUIREMENTS]: z.string().optional(),
      [FORM_FIELDS.COMPLETED_SIGNED_DOCUMENT_URL]: optionalDocument,

      [FORM_FIELDS.CARGO_DECLARATION_DOCUMENT_URL]: optionalDocument,
      [FORM_FIELDS.SHIPS_STORE_DECLARATION_DOCUMENT_URL]: optionalDocument,
      [FORM_FIELDS.CREWS_EFFECTS_DECLARATION_DOCUMENT_URL]: optionalDocument,
      [FORM_FIELDS.DANGEROUS_GOODS_MANIFEST_DOCUMENT_URL]: optionalDocument,

      passengers: z.array(z.object({ id: z.string() }).passthrough()).default([]),
      crew: isFinalSubmission
        ? z
            .array(z.object({ id: z.string() }).passthrough())
            .min(1, {
              message: t("vessel-call.falform5.at-least-one-crew"),
            })
            .default([])
        : z
            .array(z.object({ id: z.string() }).passthrough())
            .default([])
            .optional(),
    })
    .superRefine((data, ctx) => {
      if (
        data[FORM_FIELDS.PREFERRED_ANCHORAGE_AREA] &&
        !data[FORM_FIELDS.REASON_FOR_ANCHORAGE_PREFERENCE] &&
        !isDraft
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [FORM_FIELDS.REASON_FOR_ANCHORAGE_PREFERENCE],
          message: t(MANDATORY_KEY, {
            label: t(`${LANG_BASE}.${FORM_FIELDS.REASON_FOR_ANCHORAGE_PREFERENCE}`),
          }),
        });
      }
      const etaMin = toMinute(data[FORM_FIELDS.ETA]);
      const etdMin = toMinute(data[FORM_FIELDS.ETD]);
      // Find the port call with departureDate closest to today
      const portCalls = data[FORM_FIELDS.PORT_CALLS];
      if (Array.isArray(portCalls) && portCalls.length > 0) {
        if (isFinalSubmission && hasAnyPortCallDateConflicts(portCalls)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [FORM_FIELDS.PORT_CALLS],
            message: t("vessel-call.last-ten-ports.validation.unique-date"),
          });
        }

        const today = new Date();
        let closestPortCall = portCalls[0];
        let minDiff = Number.POSITIVE_INFINITY;
        for (const portCall of portCalls) {
          const depDate = portCall.departureDate ? new Date(portCall.departureDate) : null;
          if (depDate && !isNaN(depDate.getTime())) {
            const diff = Math.abs(depDate.getTime() - today.getTime());
            if (diff < minDiff) {
              minDiff = diff;
              closestPortCall = portCall;
            }
          }
        }
        if (data[FORM_FIELDS.LAST_PORT] !== closestPortCall.port && isFinalSubmission) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [FORM_FIELDS.LAST_PORT],
            message: t("vessel-call.last-ten-ports.validation.last-port-call"),
          });
        }
      }

      if (etaMin && etdMin && etdMin.getTime() <= etaMin.getTime()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [FORM_FIELDS.ETD],
          message: t("form-validation.must-be-after", {
            subject: t(`${LANG_BASE}.${FORM_FIELDS.ETD}`),
            object: t(`${LANG_BASE}.${FORM_FIELDS.ETA}`),
          }),
        });
      }

      const dateTimeOfArrival = data[FORM_FIELDS.DATE_TIME_OF_ARRIVAL];
      if (dateTimeOfArrival) {
        const dtaMin = toMinute(dateTimeOfArrival);
        const nowMin = toMinute(new Date())!;
        const oneMinuteAhead = new Date(nowMin.getTime() + 60_000);
        if (dtaMin && dtaMin.getTime() < oneMinuteAhead.getTime()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [FORM_FIELDS.DATE_TIME_OF_ARRIVAL],
            message: t("form-validation.must-be-in-future-or-present", {
              label: t("vessel-call.falform1.date-time-of-arrival"),
            }),
          });
        }
      }

      const cargoOnboard = Array.isArray(data[FORM_FIELDS.CARGO_ONBOARD])
        ? data[FORM_FIELDS.CARGO_ONBOARD]
        : [];
      const shouldValidateCargoOnboard = shouldShowCargoOnboardSection(
        data[FORM_FIELDS.VESSEL_CONDITION],
      );
      const shouldRequireCargoOnboard = shouldValidateCargoOnboard;

      if (shouldRequireCargoOnboard && cargoOnboard.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [FORM_FIELDS.CARGO_ONBOARD],
          message: t(MANDATORY_KEY, {
            label: t(`${LANG_BASE}.${FORM_FIELDS.CARGO_ONBOARD}`),
          }),
        });
      }

      if (shouldValidateCargoOnboard) {
        cargoOnboard.forEach((item, index) => {
          if (!isNonEmpty(item.cargoType)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [FORM_FIELDS.CARGO_ONBOARD, index, "cargoType"],
              message: t(MANDATORY_KEY, {
                label: t(`${LANG_BASE}.cargoType`),
              }),
            });
          }

          if (!isNonEmpty(String(item.quantity ?? ""))) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [FORM_FIELDS.CARGO_ONBOARD, index, "quantity"],
              message: t(MANDATORY_KEY, {
                label: t(`${LANG_BASE}.quantity`),
              }),
            });
          }

          if (!isNonEmpty(item.unitOfMeasurement)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [FORM_FIELDS.CARGO_ONBOARD, index, "unitOfMeasurement"],
              message: t(MANDATORY_KEY, {
                label: t(`${LANG_BASE}.unitOfMeasurement`),
              }),
            });
          }

          if (item.hazardousAndDangerousCargo && !isNonEmpty(item.imoHazardClasses)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [FORM_FIELDS.CARGO_ONBOARD, index, "imoHazardClasses"],
              message: t(MANDATORY_KEY, {
                label: t(`${LANG_BASE}.imoHazardClasses`),
              }),
            });
          }
        });
      }

      for (const { flag, specify } of specifyDependencies) {
        if (data[flag]) {
          const val = data[specify];
          if (!isNonEmpty(val) && !isDraft) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [specify],
              message: t(MANDATORY_KEY, { label: t(`${LANG_BASE}.${specify}`) }),
            });
          }
        }
      }

      if (
        data[FORM_FIELDS.PORT_FACILITY] === "others" &&
        !isNonEmpty(data[FORM_FIELDS.PORT_FACILITY_OTHER]) &&
        isFinalSubmission
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [FORM_FIELDS.PORT_FACILITY_OTHER],
          message: t(MANDATORY_KEY, {
            label: t(`${LANG_BASE}.${FORM_FIELDS.PORT_FACILITY_OTHER}`),
          }),
        });
      }

      if (data[FORM_FIELDS.ISSC_IS_VALID] && !data[FORM_FIELDS.ISSC_NUMBER] && isFinalSubmission) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [FORM_FIELDS.ISSC_NUMBER],
          message: t(MANDATORY_KEY, {
            label: t(`${LANG_BASE}.${FORM_FIELDS.ISSC_NUMBER}`),
          }),
        });
      }

      if (
        data[FORM_FIELDS.ISSC_IS_VALID] &&
        !data[FORM_FIELDS.ISSC_ISSUE_DATE] &&
        isFinalSubmission
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [FORM_FIELDS.ISSC_ISSUE_DATE],
          message: t(MANDATORY_KEY, {
            label: t(`${LANG_BASE}.${FORM_FIELDS.ISSC_ISSUE_DATE}`),
          }),
        });
      }

      if (
        data[FORM_FIELDS.ISSC_IS_VALID] &&
        !data[FORM_FIELDS.ISSC_EXPIRY_DATE] &&
        isFinalSubmission
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [FORM_FIELDS.ISSC_EXPIRY_DATE],
          message: t(MANDATORY_KEY, {
            label: t(`${LANG_BASE}.${FORM_FIELDS.ISSC_EXPIRY_DATE}`),
          }),
        });
      }

      if (
        data[FORM_FIELDS.ISSC_IS_VALID] &&
        !data[FORM_FIELDS.ISSC_ISSUED_BY] &&
        isFinalSubmission
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [FORM_FIELDS.ISSC_ISSUED_BY],
          message: t(MANDATORY_KEY, {
            label: t(`${LANG_BASE}.${FORM_FIELDS.ISSC_ISSUED_BY}`),
          }),
        });
      }

      if (
        data[FORM_FIELDS.SPECIAL_SECURITY_MEASURES] &&
        !data[FORM_FIELDS.TAKEN_MEASURES] &&
        isFinalSubmission
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [FORM_FIELDS.TAKEN_MEASURES],
          message: t(MANDATORY_KEY, {
            label: t(`${LANG_BASE}.${FORM_FIELDS.TAKEN_MEASURES}`),
          }),
        });
      }

      if (data[FORM_FIELDS.SECURITY_GUARDS_ON_BOARD]) {
        if (!data[FORM_FIELDS.NUMBER_OF_SECURITY_GUARDS] && isFinalSubmission) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [FORM_FIELDS.NUMBER_OF_SECURITY_GUARDS],
            message: t(MANDATORY_KEY, {
              label: t(`${LANG_BASE}.${FORM_FIELDS.NUMBER_OF_SECURITY_GUARDS}`),
            }),
          });
        } else if (Number(data[FORM_FIELDS.NUMBER_OF_SECURITY_GUARDS]) < 1 && isFinalSubmission) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [FORM_FIELDS.NUMBER_OF_SECURITY_GUARDS],
            message: t("form-validation.must-be-greater", {
              label: t(`${LANG_BASE}.${FORM_FIELDS.NUMBER_OF_SECURITY_GUARDS}`),
              min: 1,
            }),
          });
        }
      }

      if (data[FORM_FIELDS.ARM_AMMUNITION_ON_BOARD]) {
        if (
          !Array.isArray(data[FORM_FIELDS.ARM_AMMUNITION_PROOF_DOCUMENT_URL]) ||
          (data[FORM_FIELDS.ARM_AMMUNITION_PROOF_DOCUMENT_URL]?.length === 0 && isFinalSubmission)
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [FORM_FIELDS.ARM_AMMUNITION_PROOF_DOCUMENT_URL],
            message: t(MANDATORY_KEY, {
              label: t(`${LANG_BASE}.${FORM_FIELDS.ARM_AMMUNITION_PROOF_DOCUMENT_URL}`),
            }),
          });
        }
      }

      if (data[FORM_FIELDS.VESSEL_HAS_DEFICIENCIES]) {
        if (!data[FORM_FIELDS.DEFICIENCIES] && isFinalSubmission) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [FORM_FIELDS.DEFICIENCIES],
            message: t(MANDATORY_KEY, {
              label: t(`${LANG_BASE}.${FORM_FIELDS.DEFICIENCIES}`),
            }),
          });
        }
        if (
          !Array.isArray(data[FORM_FIELDS.DEFICIENCIES_DOCUMENTS_URL]) ||
          (data[FORM_FIELDS.DEFICIENCIES_DOCUMENTS_URL]?.length === 0 && isFinalSubmission)
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [FORM_FIELDS.DEFICIENCIES_DOCUMENTS_URL],
            message: t(MANDATORY_KEY, {
              label: t(`${LANG_BASE}.${FORM_FIELDS.DEFICIENCIES_DOCUMENTS_URL}`),
            }),
          });
        }
      }

      if (
        !Array.isArray(data[FORM_FIELDS.LAST_PORT_CLEARANCE_DOCUMENT_URL]) ||
        (data[FORM_FIELDS.LAST_PORT_CLEARANCE_DOCUMENT_URL]?.length === 0 && isFinalSubmission)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [FORM_FIELDS.LAST_PORT_CLEARANCE_DOCUMENT_URL],
          message: t(MANDATORY_KEY, {
            label: t(`${LANG_BASE}.${FORM_FIELDS.LAST_PORT_CLEARANCE_DOCUMENT_URL}`),
          }),
        });
      }

      if (data[FORM_FIELDS.PORT_FACILITY] === "OTHER") {
        const portFacilityOther = data[FORM_FIELDS.PORT_FACILITY_OTHER];
        if (!isNonEmpty(portFacilityOther) && isFinalSubmission) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [FORM_FIELDS.PORT_FACILITY_OTHER],
            message: t(MANDATORY_KEY, {
              label: t(`${LANG_BASE}.${FORM_FIELDS.PORT_FACILITY_OTHER}`),
            }),
          });
        }
      }
    });
};

export type VesselCallFormData = z.infer<ReturnType<typeof getVesselCallFormSchema>>;
